/** @jsxImportSource @emotion/react */
import { routes } from "../../../appRoutingHelpers";
import { useChallenges } from "../../../service/api";
import { SpinnerBlock } from "../../common/Spinner";
import { Table } from "../../../components/table-components/Table";
import { ChallengeRow } from "./ChallengeRow";
import { ChallengeListRowHeader } from "./ChallengeListRowHeader";
import { CollapseComponent } from "../../../components/CollapseComponent";
import { Collapse } from "antd";
import { ChallengeType } from "../../../service/api-response/Challenges";
import { ChallengeCollapseHeader } from "./ChallengeCollapseHeader";
import { CardContainer } from "../../../components/page-skeleton/CardContainer";
import { PageHeader } from "../../../components/page-skeleton/PageHeader";
import * as authSelectors from "../../auth/authSelectors";
import moment from "moment";
import { PageWrapper } from "../../../components/page-skeleton/PageWrapper";
import { ButtonInternalLink } from "../../../components/NewButtons";
import { CardBody } from "../../../components/page-skeleton/CardBody";
import { cardBottomPadding } from "../../../style-util";
import { useSelector } from "react-redux";
import { css } from "@emotion/react";
import { useHandleFetchError } from "../../../error-handler";

const listStyle = css`
  margin-top: 16px;
`;

export const ChallengesList = () => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const {
    data: challenges,
    isLoading: challengesIsLoading,
    error: listChallengesError,
  } = useChallenges(organizationKeyName);

  const challengesIsActive = (activeTo: string) => {
    return moment(activeTo).isAfter(moment());
  };

  useHandleFetchError(listChallengesError);

  if (!challenges || challengesIsLoading) {
    return <SpinnerBlock />;
  }

  const challengeids = challenges.map((c) => c.id);
  const distinctIds = [...new Set(challengeids)];
  const groupedChallenges: ChallengeType[][] = [];

  distinctIds.forEach((id) => {
    const group: ChallengeType[] = [];
    challenges.forEach((ch) => {
      if (ch.id === id) {
        group.push(ch);
      }
    });
    group.sort((a: ChallengeType, b: ChallengeType) => b.version - a.version);
    groupedChallenges.push(group);
  });

  return (
    <PageWrapper>
      <CardContainer>
        <PageHeader
          title="Challenges List"
          subtitle="Create, edit and manage all challenges. All challenge versions are listed under each challenge."
        >
          <ButtonInternalLink to={routes.challengesNew(organizationKeyName)}>
            Add new
          </ButtonInternalLink>
        </PageHeader>
        <CardBody css={cardBottomPadding}>
          <CollapseComponent>
            {groupedChallenges.map((group, index) => (
              <Collapse.Panel
                header={
                  <ChallengeCollapseHeader
                    currentIsActive={challengesIsActive(
                      group[0].activeExpiresAt
                    )}
                    title={group[0].title}
                    isDisabled={!group[0].enabled}
                    subtitle={
                      group.length +
                      (group.length === 1 ? " version" : " versions")
                    }
                  />
                }
                key={index}
              >
                <Table css={listStyle}>
                  <ChallengeListRowHeader />
                  <tbody>
                    {group.map((challenge, index) => (
                      <ChallengeRow
                        isActive={challengesIsActive(challenge.activeExpiresAt)}
                        key={index}
                        isCurrent={index === 0}
                        challenge={challenge}
                      />
                    ))}
                  </tbody>
                </Table>
              </Collapse.Panel>
            ))}
          </CollapseComponent>
        </CardBody>
      </CardContainer>
    </PageWrapper>
  );
};
